import "./chunk-MLKGABMK.js";

// src/models.ts
import { z } from "zod";
var apiError = z.object({
  code: z.number().int().gte(-2147483648).lte(2147483647),
  message: z.string()
});
var labelValue = z.object({
  label: z.string(),
  value: z.string()
}).describe("Label/value pair for dropdowns.");
var user = z.object({
  label: z.string(),
  value: z.string()
}).describe("Assignable user.");
var updateActionRequest = z.object({
  title: z.string().optional().describe("Action title."),
  description: z.string().optional().describe("Action description."),
  priority: z.union([z.literal("High"), z.literal("Normal"), z.literal("Low")]).optional().describe("Priority level."),
  dueDate: z.coerce.date().optional().describe("Suggested due date in ISO 8601 format."),
  dueLabel: z.string().optional().describe(
    'AI-generated due date confidence label (e.g. "Within 1 week").'
  ),
  entity: z.string().optional().describe("Pre-filled entity ID from BFF detection."),
  type: z.string().optional().describe("Pre-filled task type value from BFF detection."),
  subtype: z.string().optional().describe("Pre-filled task subtype value from BFF detection."),
  assigneeType: z.string().optional().describe(
    'Pre-filled assignee type (e.g. "user", "dynamic_roles"). Defaults to "user".'
  ),
  assigneeUser: labelValue.optional().describe("Pre-filled user assignee (label/value for select rendering)."),
  assigneeRole: labelValue.optional().describe("Pre-filled dynamic role (label/value for select rendering)."),
  status: z.union([
    z.literal("pending"),
    z.literal("assigned"),
    z.literal("dismissed")
  ]).optional().describe(
    "Persistence status. Absent when first generated; present once persisted."
  )
}).describe(
  "Request body for updating an action's status and assignment details. Derived from SuggestedAction."
);
var source = z.object({
  transcriptIndex: z.number().int().nonnegative().lte(4294967295).describe("Index into transcript.content[] array"),
  startChar: z.number().int().nonnegative().lte(4294967295).optional().describe("Character offset within the transcript line (start)"),
  endChar: z.number().int().nonnegative().lte(4294967295).optional().describe("Character offset within the transcript line (end)"),
  quote: z.string().optional().describe("Quote from transcript for display")
}).describe("Transcript reference for AI-generated content.");
var suggestedAction = z.object({
  id: z.string().describe("Unique identifier."),
  title: z.string().describe("Action title."),
  description: z.string().describe("Action description."),
  priority: z.union([z.literal("High"), z.literal("Normal"), z.literal("Low")]).describe("Priority level."),
  dueDate: z.coerce.date().describe("Suggested due date in ISO 8601 format."),
  dueLabel: z.string().describe('AI-generated due date confidence label (e.g. "Within 1 week").'),
  source: source.optional().describe("Transcript reference for this action."),
  entity: z.string().optional().describe("Pre-filled entity ID from BFF detection."),
  type: z.string().optional().describe("Pre-filled task type value from BFF detection."),
  subtype: z.string().optional().describe("Pre-filled task subtype value from BFF detection."),
  assigneeType: z.string().optional().describe(
    'Pre-filled assignee type (e.g. "user", "dynamic_roles"). Defaults to "user".'
  ),
  assigneeUser: labelValue.optional().describe("Pre-filled user assignee (label/value for select rendering)."),
  assigneeRole: labelValue.optional().describe("Pre-filled dynamic role (label/value for select rendering)."),
  status: z.union([
    z.literal("pending"),
    z.literal("assigned"),
    z.literal("dismissed")
  ]).optional().describe(
    "Persistence status. Absent when first generated; present once persisted."
  )
});
var suggestedUpdate = z.object({
  id: z.string().describe("Unique identifier."),
  title: z.string().describe("Display title."),
  category: z.string().describe('Category grouping (e.g. "Personal", "Financial").'),
  description: z.string().describe("Explanation of the suggested change."),
  currentValue: z.string().describe("Current value in the client file."),
  suggestedValue: z.string().describe("AI-suggested new value."),
  confidence: z.union([z.literal("High"), z.literal("Medium"), z.literal("Low")]).describe("AI confidence level for this suggestion."),
  source: source.optional().describe("Transcript reference for this suggestion."),
  status: z.union([z.literal("pending"), z.literal("approved"), z.literal("rejected")]).optional().describe(
    "Persistence status. Absent when first generated; present once persisted."
  )
});
var clientDetails = z.object({
  id: z.string(),
  name: z.string()
}).describe("Client details.");
var client = z.object({
  main: clientDetails.describe("Main client."),
  partner: clientDetails.optional().describe("Partner (if applicable).")
}).describe("Client context for an entity.");
var transcriptLine = z.object({
  timestamp: z.string(),
  text: z.string()
});
var transcript = z.object({
  content: z.array(transcriptLine)
});
var meeting = z.object({
  id: z.string().describe("Unique meeting identifier."),
  title: z.string().describe("Meeting title."),
  status: z.string().describe(
    'Current status of the meeting (e.g. "Scheduled", "In Progress", "Completed").'
  ),
  type: z.string().describe('Type of meeting (e.g. "Review", "New Client").'),
  datetime: z.coerce.date().describe("Meeting datetime in ISO 8601 format."),
  client: client.describe("Client associated with this meeting."),
  transcript: transcript.optional().describe(
    "Parsed transcript containing timestamped lines. Absent until a transcript is uploaded."
  )
}).describe("A meeting record with associated client context.");
var uploadTranscriptRequest = z.object({
  transcript: z.string().describe("The raw transcript text (VTT, SRT, or plain text).")
}).describe(
  "Request body for uploading a transcript as JSON. The endpoint also accepts multipart/form-data file uploads at runtime."
);
var summaryRequest = z.object({
  templateId: z.number().int().gte(-2147483648).lte(2147483647).optional().default(1).describe("Prompt template ID (1-4). Defaults to 1 (Discovery Meeting)."),
  additionalInstructions: z.string().optional().describe("Optional additional instructions to append to the prompt")
});
var summary = z.object({
  meetingSummary: z.string().describe("AI-generated meeting summary text."),
  suggestedUpdates: z.string().describe(
    "AI-generated client record update suggestions based on the transcript"
  ),
  suggestions: z.array(suggestedUpdate).describe("Suggested updates to client data fields."),
  actions: z.array(suggestedAction).describe("Suggested follow-up actions.")
});
var updateSuggestionRequest = z.object({
  status: z.union([
    z.literal("pending"),
    z.literal("approved"),
    z.literal("rejected")
  ]).optional().describe("New status."),
  suggestedValue: z.string().optional().describe(
    "User-edited suggested value (optional \u2014 only sent when the user edits)."
  )
}).describe(
  "Request body for updating a suggestion's status and optionally its edited value."
);
var batchUpdateSuggestionsRequest = z.object({
  status: z.union([
    z.literal("pending"),
    z.literal("approved"),
    z.literal("rejected")
  ]).describe("New status to apply to all suggestions.")
}).describe("Request body for batch status updates on suggestions.");
var batchUpdateActionsRequest = z.object({
  status: z.union([
    z.literal("pending"),
    z.literal("assigned"),
    z.literal("dismissed")
  ]).describe("New status to apply to all actions.")
}).describe("Request body for batch status updates on actions.");
var finaliseResponse = z.object({
  clientDataUpdated: z.number().int().nonnegative().lte(4294967295).describe("Number of client data fields updated."),
  actionsCreated: z.number().int().nonnegative().lte(4294967295).describe("Number of tasks created.")
});
var deliveryMethod = z.enum(["Zoom", "Microsoft Teams", "Phone", "Face-to-face"]).describe("Delivery method for a meeting.");
var createMeetingRequest = z.object({
  type: z.string().describe('Meeting type (e.g. "Review", "New Client").'),
  datetime: z.coerce.date().describe("Meeting datetime in ISO 8601 format."),
  clientId: z.number().int().gte(-2147483648).lte(2147483647).describe("Wealth API client ID."),
  clientName: z.string().describe("Display name of the client."),
  deliveryMethod: deliveryMethod.optional().describe("How the meeting is delivered."),
  isNotConfirmed: z.boolean().optional().describe("Whether the meeting has not been confirmed.")
}).describe("Request body for creating a meeting.");
var taskConfig = z.object({
  type: z.array(labelValue.describe("Label/value pair for dropdowns.")),
  subtype: z.array(labelValue.describe("Label/value pair for dropdowns."))
}).describe("Task configuration (types and subtypes).");
var tenantConfig = z.object({
  tasks: taskConfig.describe("Task configuration (types and subtypes)."),
  dynamicRoles: z.array(
    labelValue.describe("Label/value pair for dropdowns.")
  ),
  priorities: z.array(labelValue.describe("Label/value pair for dropdowns.")),
  assigneeTypes: z.array(
    labelValue.describe("Label/value pair for dropdowns.")
  )
}).describe(
  "Tenant-level configuration (practice settings, not client-specific)."
);

// src/custom.ts
import { z as z2 } from "zod";
var MAX_TRANSCRIPT_CHARS = 2e5;
var MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
var MIN_SUMMARY_CHARS = 50;
var MAX_SUMMARY_CHARS = 5e4;
var NUMBER_LOCALE = "en-AU";
var SUMMARY_LENGTH_ERROR_MESSAGE = `Summary must be between ${MIN_SUMMARY_CHARS.toLocaleString(NUMBER_LOCALE)} and ${MAX_SUMMARY_CHARS.toLocaleString(NUMBER_LOCALE)} characters.`;
var SUMMARY_MIN_ERROR_MESSAGE = `Summary must be at least ${MIN_SUMMARY_CHARS.toLocaleString(NUMBER_LOCALE)} characters`;
var SUMMARY_MAX_ERROR_MESSAGE = `Summary must not exceed ${MAX_SUMMARY_CHARS.toLocaleString(NUMBER_LOCALE)} characters`;
var ALLOWED_MIME_TYPES = /* @__PURE__ */ new Set([
  "text/plain",
  "text/vtt",
  "application/x-subrip"
]);
var ALLOWED_EXTENSIONS = /* @__PURE__ */ new Set([".txt", ".vtt", ".srt"]);
var transcriptUpload = uploadTranscriptRequest.extend({
  transcript: z2.string().trim().min(1, "Transcript must not be empty").max(
    MAX_TRANSCRIPT_CHARS,
    `Transcript must not exceed ${MAX_TRANSCRIPT_CHARS.toLocaleString()} characters`
  )
});
var transcriptFile = z2.object({
  mimetype: z2.string(),
  filename: z2.string().optional(),
  content: z2.string().trim().min(1, "Uploaded transcript file is empty.")
}).refine(
  ({ mimetype, filename }) => {
    if (ALLOWED_MIME_TYPES.has(mimetype)) return true;
    const unknownMime = !mimetype || mimetype === "application/octet-stream";
    if (unknownMime && filename) {
      const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
      return ALLOWED_EXTENSIONS.has(ext);
    }
    return false;
  },
  {
    message: `Unsupported file type. Accepted formats: ${[...ALLOWED_MIME_TYPES].join(", ")}`
  }
).refine(({ content }) => content.length <= MAX_TRANSCRIPT_CHARS, {
  message: `Transcript must not exceed ${MAX_TRANSCRIPT_CHARS.toLocaleString()} characters`
});
var summaryRequestStrict = summaryRequest.extend({
  templateId: z2.number().int().min(1).max(4).default(1),
  additionalInstructions: z2.string().max(2e3, "Additional instructions must not exceed 2,000 characters").optional().default("")
});
var summaryOutputStrict = z2.object({
  summary: summary.shape.meetingSummary.min(MIN_SUMMARY_CHARS, SUMMARY_MIN_ERROR_MESSAGE).max(MAX_SUMMARY_CHARS, SUMMARY_MAX_ERROR_MESSAGE)
});
var updateSuggestionRequestStrict = updateSuggestionRequest.strict().refine((obj) => Object.keys(obj).length > 0, {
  message: "At least one field must be provided"
});
var updateActionRequestStrict = updateActionRequest.strict().refine((obj) => Object.keys(obj).length > 0, {
  message: "At least one field must be provided"
});

// src/xplan-roles.ts
var ENTITY_RESPONSIBILITY_ROLES = [
  { value: 1, label: "Client Adviser" },
  { value: 100, label: "Secondary Adviser" },
  { value: 200, label: "Paraplanner" },
  { value: 300, label: "Administrator" },
  { value: 350, label: "Insurance Adviser" },
  { value: 360, label: "Accountant" },
  { value: 370, label: "General Insurance Adviser" },
  { value: 380, label: "Investment Adviser" },
  { value: 390, label: "Mortgage Adviser" },
  { value: 395, label: "Client Services Manager" }
];

// src/xplan-priorities.ts
var TASK_PRIORITIES = [
  { value: 0, label: "Low" },
  { value: 1, label: "Normal" },
  { value: 2, label: "High" }
];

// src/xplan-assignee-types.ts
var TASK_ASSIGNEE_TYPES = [
  { value: "user", label: "User" },
  { value: "dynamic_roles", label: "Role" }
  // { value: 'entity_responsibility', label: 'Entity Responsibility' },
  // { value: 'workflow_group', label: 'Workflow Group' },
  // { value: 'assignee_of_prev_task', label: 'Previous Task Assignee' },
];
export {
  ALLOWED_EXTENSIONS,
  ALLOWED_MIME_TYPES,
  ENTITY_RESPONSIBILITY_ROLES,
  MAX_FILE_SIZE_BYTES,
  MAX_SUMMARY_CHARS,
  MAX_TRANSCRIPT_CHARS,
  MIN_SUMMARY_CHARS,
  SUMMARY_LENGTH_ERROR_MESSAGE,
  SUMMARY_MAX_ERROR_MESSAGE,
  SUMMARY_MIN_ERROR_MESSAGE,
  TASK_ASSIGNEE_TYPES,
  TASK_PRIORITIES,
  apiError,
  batchUpdateActionsRequest,
  batchUpdateSuggestionsRequest,
  client,
  clientDetails,
  createMeetingRequest,
  deliveryMethod,
  finaliseResponse,
  labelValue,
  meeting,
  source,
  suggestedAction,
  suggestedUpdate,
  summary,
  summaryOutputStrict,
  summaryRequest,
  summaryRequestStrict,
  taskConfig,
  tenantConfig,
  transcript,
  transcriptFile,
  transcriptLine,
  transcriptUpload,
  updateActionRequest,
  updateActionRequestStrict,
  updateSuggestionRequest,
  updateSuggestionRequestStrict,
  uploadTranscriptRequest,
  user
};
